import { motion } from 'framer-motion';
import type React from 'react';
import type { Novel } from '../../data/samples';
import type { ReadingHistoryItem } from '../../hooks/useReadingHistory';
import { ArrowRightIcon, BookmarkIcon } from '../Icons';
import type { NovelCoverConfig } from './NovelCard';
import { NovelCover } from './NovelCover';

interface LatestBookmarkCardProps {
  latestNovel: Novel | null;
  latestHistory?: ReadingHistoryItem;
  coverConfig?: NovelCoverConfig;
  onSelectNovel: (novel: Novel) => void;
  isDark: boolean;
  cardBg: string;
  borderCol: string;
  textMuted: string;
}

export const LatestBookmarkCard: React.FC<LatestBookmarkCardProps> = ({
  latestNovel,
  latestHistory,
  coverConfig,
  onSelectNovel,
  isDark,
  textMuted,
}) => {
  const progressPercent = Math.round(latestHistory?.progressPercentage || 0);

  return (
    <motion.div
      whileHover={{
        y: -4,
        scale: 1.005,
      }}
      transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
      className={`col-span-1 md:col-span-2 p-5 rounded-3xl border relative overflow-hidden group backdrop-blur-xl transition-all duration-300 ${
        isDark
          ? 'border-white/10 bg-gradient-to-br from-[#1A1A1E]/90 via-[#141416]/90 to-[#0F0F11]/90 shadow-[0_12px_40px_rgba(0,0,0,0.5)]'
          : 'border-black/5 bg-gradient-to-br from-white/90 via-[#FAFAFA]/90 to-[#F5F5F0]/90 shadow-[0_10px_35px_rgba(0,0,0,0.05)]'
      }`}
    >
      {/* アンバーのほのかな背景アクセントグラデーション */}
      <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-amber-500/10 blur-3xl pointer-events-none transition-all group-hover:bg-amber-500/20" />

      {latestNovel && latestHistory ? (
        <>
          <div className="flex items-center justify-between mb-3.5 relative z-10">
            <span
              className={`text-xs font-bold tracking-widest px-3 py-1 rounded-full uppercase flex items-center space-x-1.5 backdrop-blur-md ${
                isDark
                  ? 'bg-amber-500/20 border border-amber-500/40 text-amber-300'
                  : 'bg-amber-100 border border-amber-300 text-amber-900'
              }`}
            >
              <BookmarkIcon size={12} strokeWidth={2.5} />
              <span>前回のしおり</span>
            </span>
            <span className={`text-xs font-mono font-semibold ${textMuted}`}>
              最終読書: {new Date(latestHistory.updatedAt).toLocaleDateString()}
            </span>
          </div>

          <button
            type="button"
            className="flex space-x-4 items-center cursor-pointer w-full text-left bg-transparent border-none p-0 font-sans group/btn relative z-10"
            onClick={() => onSelectNovel(latestNovel)}
          >
            <div className="w-14 shrink-0 shadow-[0_8px_20px_rgba(0,0,0,0.3)] rounded-lg overflow-hidden transition-all duration-300 group-hover/btn:scale-105 group-hover/btn:shadow-[0_12px_28px_rgba(245,158,11,0.25)]">
              <NovelCover novel={latestNovel} config={coverConfig} isSmall />
            </div>

            <div className="flex-1 min-w-0">
              <h2 className="font-serif font-bold text-base truncate group-hover/btn:text-amber-500 dark:group-hover/btn:text-amber-400 transition-colors">
                {latestNovel.title}
              </h2>

              <p className={`text-xs ${textMuted} truncate mt-0.5 flex items-center font-medium`}>
                <span>{latestNovel.author}</span>
                {latestNovel.scraped && (
                  <>
                    <span className="mx-1.5 opacity-40">•</span>
                    <span className="text-xs text-amber-700 dark:text-amber-300 font-bold px-1.5 py-0.5 rounded bg-amber-500/20">
                      なろう
                    </span>
                  </>
                )}
              </p>

              {/* 進捗バー & 数値 */}
              <div className="flex items-center space-x-2.5 mt-2.5">
                <div className="flex-1 h-2 bg-black/10 dark:bg-white/10 rounded-full overflow-hidden p-0.5">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-amber-500 via-amber-400 to-yellow-300 transition-all duration-500"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
                <span className="text-xs font-mono font-bold w-9 text-right text-amber-700 dark:text-amber-300">
                  {progressPercent}%
                </span>
              </div>

              <p className={`text-xs ${textMuted} mt-1.5 truncate font-serif italic opacity-90`}>
                続き: {latestHistory.sectionTitle}
              </p>
            </div>

            <div
              className={`p-3.5 rounded-2xl border transition-all duration-300 group-hover/btn:translate-x-1 group-hover/btn:bg-amber-500 group-hover/btn:text-white group-hover/btn:border-amber-500 shadow-sm ${
                isDark
                  ? 'bg-white/5 border-white/10 text-[#FAF8F5]'
                  : 'bg-black/5 border-black/5 text-[#111111]'
              }`}
            >
              <ArrowRightIcon size={16} strokeWidth={2.5} />
            </div>
          </button>
        </>
      ) : (
        <div className="h-full flex flex-col justify-center items-center py-6 text-center relative z-10">
          <div className="p-3.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-500 mb-2.5">
            <BookmarkIcon size={22} strokeWidth={2} />
          </div>
          <p className="font-serif text-xs font-bold opacity-80">しおりはまだありません</p>
          <p className={`text-[11px] ${textMuted} mt-1 max-w-[220px]`}>
            小説を読み進めると自動的にしおりが挟まれます。
          </p>
        </div>
      )}
    </motion.div>
  );
};
